import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import PollenDotsNav from '@/components/PollenDotsNav';
import { API, useAuth } from '@/App';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Search, Loader2, MapPin, Users, CalendarDays, ArrowRight, FileSpreadsheet } from 'lucide-react';
import { formatDate } from '@/lib/dateUtils';
import BulkActionBar from '@/components/BulkActionBar';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';

const statusColors = {
  Planning: 'bg-gray-100 text-gray-800',
  Booked: 'bg-emerald-100 text-emerald-800',
  Ongoing: 'bg-amber-100 text-amber-800',
  Completed: 'bg-emerald-100 text-emerald-800',
  Cancelled: 'bg-rose-100 text-rose-700',
};

const PollenDotsTours = () => {
  const POLLEN_API = `${API}/pollen`;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  // FY period filter — buckets tours by invoice_date into Current FY /
  // Previous FY. "All" shows everything regardless of date. Historical-flagged
  // tours are forced to behave like Previous FY on the Current tab (they're
  // excluded) and always appear on the Previous tab.
  const [periodFilter, setPeriodFilter] = useState('all');
  // Bulk-action state — selected tour IDs for bulk delete.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const authFetch = (url, opts = {}) => {
    const h = {}; const t = localStorage.getItem('session_token'); if (t) h['Authorization'] = `Bearer ${t}`;
    return fetch(url, { ...opts, credentials: 'include', headers: { ...opts.headers, ...h } });
  };

  const fetchTours = useCallback(async () => {
    try {
      const res = await authFetch(`${POLLEN_API}/tours${statusFilter ? `?status=${statusFilter}` : ''}`);
      if (res.ok) setTours(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { fetchTours(); }, [fetchTours]);

  // Compute current FY window (India FY: Apr 1 → Mar 31). The tours array is
  // small enough (<2k) to bucket client-side; no extra round-trip needed.
  const today = new Date();
  const fyStartYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  const currFYStart = `${fyStartYear}-04-01`;
  const currFYEnd = `${fyStartYear + 1}-04-01`;
  const prevFYStart = `${fyStartYear - 1}-04-01`;
  const prevFYEnd = currFYStart;

  const filtered = tours.filter(t => {
    const s = search.toLowerCase();
    if (s && !(t.tour_name?.toLowerCase().includes(s) || t.customer_name?.toLowerCase().includes(s) || t.destination?.toLowerCase().includes(s))) return false;
    if (periodFilter === 'all') return true;
    const inv = (t.invoice_date || '').slice(0, 10);
    if (periodFilter === 'current') {
      // Historical tours are explicitly yanked out of current-FY view even
      // if their invoice_date happens to fall inside — matches the dashboard.
      if (t.is_historical) return false;
      return !!inv && inv >= currFYStart && inv < currFYEnd;
    }
    if (periodFilter === 'previous') {
      // Anything marked Historical always shows here. Otherwise bucket by
      // invoice_date falling in last FY's window.
      if (t.is_historical) return true;
      return !!inv && inv >= prevFYStart && inv < prevFYEnd;
    }
    return true;
  });

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      if (filtered.length > 0 && filtered.every(t => prev.has(t.id))) return new Set();
      return new Set(filtered.map(t => t.id));
    });
  };
  const handleBulkDelete = async () => {
    setBulkBusy(true);
    try {
      const res = await authFetch(`${POLLEN_API}/tours/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tour_ids: Array.from(selectedIds) }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Delete failed');
      const d = await res.json();
      toast.success(`Deleted ${d.count} tour${d.count === 1 ? '' : 's'}`);
      setSelectedIds(new Set());
      setBulkDeleteConfirm(false);
      fetchTours();
    } catch (e) { toast.error(e.message); }
    setBulkBusy(false);
  };

  if (!['Pollen Dots User','Super Admin','Admin','Operator','Intern'].includes(user?.role)) return <Navigate to="/dashboard" replace />;
  if (loading) return <Layout><div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;

  const repairNames = async () => {
    if (!confirm('Scan all tours and repair stale auto-names whose month/year doesn\'t match the start date?')) return;
    try {
      const res = await authFetch(`${POLLEN_API}/tours/backfill-stale-names`, { method: 'POST' });
      if (!res.ok) throw new Error('Failed');
      const d = await res.json();
      if (d.count === 0) toast.success('All tour names are already in sync.');
      else toast.success(`Repaired ${d.count} stale tour name${d.count === 1 ? '' : 's'}.`);
      fetchTours();
    } catch { toast.error('Could not repair tour names'); }
  };

  const downloadOpsSchedule = async () => {
    try {
      const res = await authFetch(`${POLLEN_API}/ops-schedule.xlsx?days=90`);
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pollen-ops-schedule-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Ops schedule downloaded — check-ins, check-outs & pickups for the next 90 days');
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Layout>
      <div className="space-y-4" data-testid="tours-list-page">
        <PollenDotsNav />
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h1 className="text-2xl font-bold tracking-tight">Tours</h1>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={downloadOpsSchedule} data-testid="ops-schedule-btn" title="Excel of upcoming hotel check-ins/check-outs and transport pickups, customer-wise (next 90 days)"><FileSpreadsheet className="w-4 h-4 mr-1 text-emerald-700" />Check-ins & Pickups</Button>
            <Button variant="outline" size="sm" onClick={repairNames} data-testid="repair-tour-names-btn" title="Repair tour names where month/year doesn't match start date">Repair stale names</Button>
            <Button onClick={() => navigate('/pollen-dots/add-tour')} data-testid="add-tour-btn"><Plus className="w-4 h-4 mr-1" />Add Tour</Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mr-1">Period</span>
          {[
            { k: 'current',  label: `Current FY (${fyStartYear}-${fyStartYear + 1})` },
            { k: 'previous', label: `Previous FY (${fyStartYear - 1}-${fyStartYear})` },
            { k: 'all',      label: 'All Tours' },
          ].map(opt => (
            <Button key={opt.k} variant={periodFilter === opt.k ? 'default' : 'outline'} size="sm"
              onClick={() => setPeriodFilter(opt.k)}
              data-testid={`period-filter-${opt.k}`}
            >{opt.label}</Button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search tours..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} data-testid="tour-search" />
          </div>
          {['', 'Planning', 'Booked', 'Ongoing', 'Completed', 'Cancelled'].map(s => (
            <Button key={s} variant={statusFilter === s ? 'default' : 'outline'} size="sm" onClick={() => setStatusFilter(s)} data-testid={`filter-${s || 'all'}`}>
              {s || 'All'}
            </Button>
          ))}
        </div>
        {filtered.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">No tours found</CardContent></Card>
        ) : (
          <>
            {/* Mobile Cards */}
            <div className="sm:hidden space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              {filtered.map(t => (
                <Card key={t.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={(e) => { if (e.target.closest('[data-row-checkbox]')) return; navigate(`/pollen-dots/tour/${t.id}`); }}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0" data-row-checkbox>
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-primary cursor-pointer shrink-0"
                          checked={selectedIds.has(t.id)}
                          onChange={(e) => { e.stopPropagation(); toggleSelect(t.id); }}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`select-tour-${t.id}`}
                        />
                        <span className="font-medium text-sm truncate">{t.tour_name}</span>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        {t.is_historical && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0">Historical</Badge>}
                        <Badge className={statusColors[t.status]}>{t.status}</Badge>
                      </div>
                    </div>
                    <div className="flex gap-3 text-xs text-muted-foreground">
                      <span><MapPin className="w-3 h-3 inline mr-1" />{t.destination || '-'}</span>
                      <span><CalendarDays className="w-3 h-3 inline mr-1" />{formatDate(t.start_date)}</span>
                      <span><Users className="w-3 h-3 inline mr-1" />{(t.adults || 0) + (t.children || 0)}</span>
                    </div>
                    <div className="text-xs font-medium text-right">{(t.customer_amount || 0).toLocaleString('en-IN')}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {/* Desktop Table */}
            <div className="hidden sm:block overflow-x-auto max-h-[70vh] overflow-y-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="w-10">
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-primary cursor-pointer"
                      checked={filtered.length > 0 && filtered.every(t => selectedIds.has(t.id))}
                      onChange={toggleSelectAll}
                      data-testid="select-all-tours"
                      title="Select all"
                    />
                  </TableHead>
                  <TableHead>Tour</TableHead><TableHead>Customer</TableHead><TableHead>Destination</TableHead>
                  <TableHead>Dates</TableHead><TableHead>Status</TableHead><TableHead>Source</TableHead>
                  <TableHead className="text-right">Amount</TableHead><TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {filtered.map(t => (
                    <TableRow key={t.id} className={`cursor-pointer hover:bg-muted/50 ${selectedIds.has(t.id) ? 'bg-primary/5' : ''}`} onClick={() => navigate(`/pollen-dots/tour/${t.id}`)} data-testid={`tour-row-${t.id}`}>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-primary cursor-pointer"
                          checked={selectedIds.has(t.id)}
                          onChange={() => toggleSelect(t.id)}
                          data-testid={`select-tour-${t.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{t.tour_name}</TableCell>
                      <TableCell className="text-muted-foreground">{t.customer_name}</TableCell>
                      <TableCell className="text-muted-foreground">{t.destination || '-'}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">{formatDate(t.start_date)} to {formatDate(t.end_date)}</TableCell>
                      <TableCell><div className="flex gap-1 items-center"><Badge className={statusColors[t.status]}>{t.status}</Badge>{t.is_historical && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0">Historical</Badge>}</div></TableCell>
                      <TableCell className="text-muted-foreground text-xs">{t.source_type}</TableCell>
                      <TableCell className="text-right font-medium">{(t.customer_amount || 0).toLocaleString('en-IN')}</TableCell>
                      <TableCell><ArrowRight className="w-4 h-4 text-muted-foreground" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        <BulkActionBar
          selectedCount={selectedIds.size}
          onClear={() => setSelectedIds(new Set())}
          onDelete={() => setBulkDeleteConfirm(true)}
          deleting={bulkBusy}
        />
        <ConfirmDeleteDialog
          open={bulkDeleteConfirm}
          onClose={() => setBulkDeleteConfirm(false)}
          onConfirm={handleBulkDelete}
          title={`Delete ${selectedIds.size} tour${selectedIds.size === 1 ? '' : 's'}?`}
          description="This will permanently delete every selected tour. This cannot be undone."
        />
      </div>
    </Layout>
  );
};

export default PollenDotsTours;
